import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ListingCarousel } from '../ListingCarousel';
import type { ListingCardProps } from '../ListingCard';
import { AssistantMark } from './AssistantMark';
import { TypingDots } from './TypingDots';
import { SearchNarration } from '../../utils/searchNarration';
export type LiveSearchStatus = 'running' | 'done' | 'stopped';
interface LiveSearchResponseProps {
  narration: SearchNarration;
  /** Everything the search will return; the first three arrive one by one. */
  listings: ListingCardProps[];
  startedAt: number;
  durationMs: number;
  status: LiveSearchStatus;
  /** How many cards had arrived when the customer pressed Stop. */
  revealedAtStop?: number;
  onComplete: () => void;
  onStop: (revealed: number) => void;
  /** Called whenever new content appears, so the thread can follow it. */
  onProgress: () => void;
  /** Replace with the Talab avatar when it exists. */
  avatar?: React.ReactNode;
}

/* Reference timeline for a ~10 second search. Faster searches compress it
   proportionally so every stage is still seen; slower ones keep these times
   and wait with the full row of results showing. */
const REFERENCE_MS = 5000;
const ACK_AT = 250;
const STATUS_AT = 450;
const SKELETONS_AT = 600;
/** When each result is "found" — a brisk cadence, first one under ~1s. */
const REVEAL_AT = [1000, 1600, 2200, 3000, 3800];
/** Two more slots open once the first three have landed. */
const MORE_SLOTS_AT = 2200;
/** The one status change allowed, near the end of a long search. */
const LONG_AT = 8800;
/** Stop is offered only once a search is genuinely overrunning. */
const STOP_AT = 10000;
const EASE = [0.23, 1, 0.32, 1] as const;

/**
 * One search, told as a conversation:
 *   1. Talab acknowledges — a bubble that never changes once said.
 *   2. A small status line (not a bubble) while the products fill in.
 *   3. Placeholder cards that turn into real products one by one.
 *   4. The status disappears and Talab sends a NEW closing message.
 */
export function LiveSearchResponse({
  narration,
  listings,
  startedAt,
  durationMs,
  status,
  revealedAtStop = 0,
  onComplete,
  onStop,
  onProgress,
  avatar
}: LiveSearchResponseProps) {
  const running = status === 'running';
  const [now, setNow] = useState(() => Date.now());
  const completed = useRef(false);
  useEffect(() => {
    if (!running) return;
    const id = window.setInterval(() => setNow(Date.now()), 50);
    return () => window.clearInterval(id);
  }, [running]);
  const elapsed = now - startedAt;
  const scale = Math.min(1, durationMs / REFERENCE_MS);
  const at = (ms: number) => ms * scale;
  useEffect(() => {
    if (running && elapsed >= durationMs && !completed.current) {
      completed.current = true;
      onComplete();
    }
  }, [running, elapsed, durationMs, onComplete]);
  const acknowledged = !running || elapsed >= Math.max(150, at(ACK_AT));
  const statusShown = running && elapsed >= at(STATUS_AT);
  const skeletonsShown = !running || elapsed >= at(SKELETONS_AT);
  // Only a search that genuinely runs long changes its status — never a fast one.
  const isLong = running && durationMs > LONG_AT && elapsed >= LONG_AT;
  const canStop = running && elapsed >= STOP_AT;
  const revealedWhileRunning = Math.min(listings.length, REVEAL_AT.filter(r => elapsed >= at(r)).length);
  const revealed = status === 'done' ? listings.length : status === 'stopped' ? revealedAtStop : revealedWhileRunning;
  const slots = elapsed >= at(MORE_SLOTS_AT) ? REVEAL_AT.length : 3;
  const rowListings = status === 'done' ? listings : status === 'stopped' ? listings.slice(0, revealedAtStop) : listings.slice(0, slots);
  const showRow = rowListings.length > 0 && skeletonsShown;
  const closing = status === 'done' ? narration.closing : status === 'stopped' ? revealedAtStop > 0 ? 'I stopped looking. Here’s what I found so far — tell me a little more and I’ll search again.' : 'I stopped looking. Tell me a little more about what you need and I’ll search again.' : null;

  // Follow new content — the thread decides whether the customer wants that.
  const stage = `${status}-${acknowledged}-${statusShown}-${skeletonsShown}-${slots}-${revealed}-${isLong}`;
  useEffect(() => {
    if (!acknowledged) return;
    const raf = requestAnimationFrame(onProgress);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);
  if (!acknowledged) return null;
  const statusText = isLong ? narration.long : narration.searching;
  return <div className="mb-6" aria-busy={running} data-id="element-3378" data-mp="1aopkm1ymrguq">
      {/* 1. The acknowledgement — said once, never rewritten. */}
      <AssistantBubble avatar={avatar} data-id="element-3379" data-mp="myvb9udisaf8">{narration.ack}</AssistantBubble>

      {/* 2. Temporary activity — collapses away when the search ends. */}
      <AnimatePresence initial={false} data-id="element-3380" data-mp="hyha8wac60qp">
        {statusShown && <motion.div key="status" initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.22,
        ease: EASE
      }} className="overflow-hidden" data-id="element-3381" data-mp="2tn3zt82kdu7m">
            <div className="mt-2.5 ml-14 flex items-center justify-between gap-3 pr-4 h-6" role="status" aria-live="polite" data-id="element-3382" data-mp="2pmtt1t6y6zrp">
              <p className="flex items-center min-w-0 text-[12.5px] font-medium" data-id="element-3383" data-mp="3hcgg0rwb72jf">
                {/* Light passes through the words while Talab looks. */}
                <AnimatePresence mode="wait" initial={false} data-id="element-3384" data-mp="38ctdu3vr9spb">
                  <motion.span key={statusText} className="truncate search-shimmer-text" initial={{
                opacity: 0,
                y: 3
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -3
              }} transition={{
                duration: 0.2,
                ease: EASE
              }} data-id="element-3385" data-mp="38b8kghl6gft1">
                    {statusText}
                  </motion.span>
                </AnimatePresence>
                <TypingDots data-id="element-3386" data-mp="142ooo40kgbkz" />
              </p>
              {canStop && <button onClick={() => onStop(revealedWhileRunning)} className="flex-shrink-0 px-2.5 py-1 rounded-lg text-[12.5px] font-medium text-slate-500 hover:text-dark-navy hover:bg-slate-100 transition-colors duration-150 ease-out" data-id="element-3387" data-mp="z451y1e5evrk">
                  Stop
                </button>}
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* 3. Products arriving one by one into fixed-size slots. */}
      {showRow && <div className="mt-3 ml-0 md:ml-14" data-id="element-3388" data-mp="2fiwvwiqy0b2z">
          <ListingCarousel listings={rowListings} pendingFrom={running ? revealed : undefined} data-id="element-3389" data-mp="2fhlugpkyzff1" />
        </div>}

      {/* 4. A new message once the results are in. */}
      {closing && <div className="mt-5" data-id="element-3390" data-mp="2n1wggrq548nx">
          <AssistantBubble avatar={avatar} delay={0.08} data-id="element-3391" data-mp="2n14hr818xutf">
            {closing}
          </AssistantBubble>
        </div>}
    </div>;
}
function AssistantBubble({
  children,
  avatar,
  delay = 0
}: {
  children: React.ReactNode;
  avatar?: React.ReactNode;
  delay?: number;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 4
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.25,
    ease: EASE,
    delay
  }} className="flex gap-4" data-id="element-3392" data-mp="1z4qrv80dsxpj">
      {avatar ?? <AssistantMark data-id="element-3393" data-mp="291lkxbdvy5wf" />}
      <div className="flex-1 min-w-0" data-id="element-3394" data-mp="291bxnvk86cuf">
        <div className="inline-block max-w-full bg-white rounded-2xl rounded-tl-none px-4 py-3.5 shadow-soft border border-slate-100" data-id="element-3395" data-mp="294hk131gz0mg">
          <p className="text-slate-700 text-[15px] leading-relaxed" data-id="element-3396" data-mp="3ki1w2sieq0dh">
            {children}
          </p>
        </div>
      </div>
    </motion.div>;
}